/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Col, Collapse, Row, Select, Spin } from "antd";
import styled from "styled-components";
import { isEmpty, isString, noop } from "lodash";
import { useDebouncedCallback } from "use-debounce";
import { LoadingOutlined } from "@ant-design/icons";

import { RAImage, RATypography } from "../customs";

import { getAirports, getAirportDetail } from "@/stores/apis/apiAirport";
import { SvgLocation } from "@/assets/icons";
import { NoResults } from "../customs/NoResults";
import { IconArrowDown, IconArrowUp } from "@/assets/images";

const { Panel } = Collapse;

const airportName = (airport) => {
	let name = airport?.icao_code;
	if (airport?.iata_code) name += ` - ${airport.iata_code}`;
	name += ` - ${airport.name}`;
	return name;
};

const AirportItem = ({ airport = null, onClick = noop, isHeaderCollapse = false }) => {
	return (
		airport && (
			<div onClick={onClick} style={{ cursor: "pointer" }}>
				<RATypography truncate>{airportName(airport)}</RATypography>
				<Row
					align="middle"
					justify="space-between"
					style={{
						marginTop: 12,
						marginRight: isHeaderCollapse ? -26 : 0,
					}}
					gutter={[8, 2]}
				>
					<Col>
						<RATypography color="#FFB11A" truncate>
							{airport.location?.city}
						</RATypography>
					</Col>
					<Col>
						<RATypography color="#FFB11A" truncate>
							{airport.location?.country?.english_name}
						</RATypography>
					</Col>
				</Row>
			</div>
		)
	);
};

const NearAirportItem = ({ near = null, onClick = noop }) => {
	return (
		near?.airport && (
			<NearAirportWrapper onClick={onClick}>
				<RATypography truncate>{airportName(near.airport)}</RATypography>
				<RATypography top={4}>{`${near.distance} mi`}</RATypography>
			</NearAirportWrapper>
		)
	);
};

const Label = ({ onSelect = noop, airport = null }) => {
	const [activeKey, setActiveKey] = useState(null);

	const handleExpandLabel = ({ e, isActive, panelKey }) => {
		e.stopPropagation();
		if (isActive) {
			setActiveKey(null);
		} else {
			setActiveKey(panelKey);
		}
	};

	const nearAirports = airport?.near_airports?.filter((a) => a.airport);

	return (
		<div>
			{nearAirports?.length > 0 ? (
				<CollapseLabel
					defaultActiveKey={[]}
					expandIconPosition="end"
					ghost
					bordered={false}
					expandIcon={({ isActive }) => (
						<CollapseArrow
							onClick={(e) =>
								handleExpandLabel({
									e,
									isActive,
									panelKey: airport._id,
								})
							}
						>
							{isActive ? (
								<RAImage width="12px" height="12px" url={IconArrowUp} />
							) : (
								<RAImage width="12px" height="12px" url={IconArrowDown} />
							)}
						</CollapseArrow>
					)}
					activeKey={activeKey}
				>
					<Panel
						header={<AirportItem isHeaderCollapse airport={airport} />}
						key={airport._id}
						collapsible="icon"
					>
						{nearAirports.map((near) => (
							<NearAirportItem
								key={near.airport?._id}
								near={near}
								onClick={(e) => {
									e.stopPropagation();
									onSelect?.(near.airport);
								}}
							/>
						))}
					</Panel>
				</CollapseLabel>
			) : (
				<div style={{ padding: "12px 20px" }}>
					<AirportItem airport={airport} />
				</div>
			)}
		</div>
	);
};

export const RAInputSearchAirport = forwardRef(
	(
		{
			value = null,
			onSelect = noop,
			prefix = null,
			title = null,
			error = null,
			"data-testid": dataTestId = null,
			...props
		},
		forwardedRef,
	) => {
		const wrapperRef = useRef(null);
		const dropdownRef = useRef(null);

		const [loading, setLoading] = useState(false);
		const [loadingGetAirportDetail, setLoadingGetAirportDetail] = useState(false);
		const [searchValue, setSearchValue] = useState(null);
		const [options, setOptions] = useState([]);
		const [open, setOpen] = useState(false);

		useEffect(() => {
			const handler = (e) => {
				if (
					wrapperRef.current &&
					!wrapperRef.current.contains?.(e.target) &&
					dropdownRef.current &&
					!dropdownRef.current.contains?.(e.target)
				) {
					setOpen(false);
				}
			};

			document.addEventListener("mousedown", handler);
			document.addEventListener("touchstart", handler);

			return () => {
				document.removeEventListener("mousedown", handler);
				document.removeEventListener("touchstart", handler);
			};
		}, []);

		const handleTransformOptions = (aps) => {
			return aps.map((airport) => ({
				...airport,
				item: `[${airport.icao_code}] - ${airport.name}`,
				label: (
					<Label
						onSelect={(air) => {
							onSelect(air);
							setOpen(false);
						}}
						airport={airport}
					/>
				),
			}));
		};

		const handleGetAirportDetail = async (airportId) => {
			setLoadingGetAirportDetail(true);
			const rs = await getAirportDetail(airportId);
			if (rs.success && rs.data?.data) {
				const ops = handleTransformOptions([rs.data?.data]);
				setOptions(ops);
				onSelect(ops[0]);
			}
			setLoadingGetAirportDetail(false);
		};

		useEffect(() => {
			if (value) {
				const valueId = value?._id || value;
				if (isString(valueId)) {
					const valueInOps = options.find((o) => o._id === valueId);
					if (valueInOps) {
						const ops = handleTransformOptions([valueInOps]);
						setOptions(ops ?? []);
					} else {
						if (value?._id) {
							(async () => {
								setLoadingGetAirportDetail(true);
								const ap = await getAirportDetail(value._id);
								if (ap.success && ap.data?.data) {
									const ops = handleTransformOptions([ap.data?.data]);
									setOptions(ops);
								}
								setLoadingGetAirportDetail(false);
							})();
						} else {
							handleGetAirportDetail(valueId);
						}
					}
				}
			}
		}, [value]);

		const handleSearch = useDebouncedCallback(async (key) => {
			const keyTrim = key?.trim();
			setSearchValue(keyTrim);
			if (keyTrim?.length > 1) {
				setLoading(true);
				setOptions([]);
				const rs = await getAirports({
					key: keyTrim,
					paging: false,
					limit: 1,
				});
				const aps = handleTransformOptions(rs?.data?.data?.docs ?? []);
				setOptions(aps ?? []);
				setLoading(false);
			}
		}, 500);

		useImperativeHandle(forwardedRef, () => ({
			triggerSearch: (key) => handleSearch(key),
		}));

		return (
			<div>
				{title && (
					<RATypography bottom={8} color="#595959" variant="sub-heading4">
						{title}
					</RATypography>
				)}
				<div ref={wrapperRef} style={{ position: "relative" }}>
					{loadingGetAirportDetail && (
						<LoadingElement>
							<Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
						</LoadingElement>
					)}
					<StyledSelect
						open={open}
						allowClear
						showSearch
						onSearch={handleSearch}
						value={value?._id ? value._id : value || null}
						options={options}
						optionLabelProp="item"
						onChange={(_, o) => {
							onSelect(o);
							setOpen(false);
						}}
						placeholder="Search airport"
						fieldNames={{
							label: "label",
							value: "_id",
							options: "options",
						}}
						filterOption={false}
						notFoundContent={
							loading ? (
								<div
									style={{
										margin: 24,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
								</div>
							) : (
								<NoResults
									description={
										isEmpty(searchValue)
											? "Please enter some characters to search"
											: searchValue?.trim()?.length <= 1
												? "Please enter at least 2 characters to search"
												: "Oops! You don't find any airport"
									}
								/>
							)
						}
						dropdownRender={(originNode) => (
							<DropdownRender className="ra-search-airport-ant-select-dropdown" ref={dropdownRef}>
								{originNode}
							</DropdownRender>
						)}
						suffixIcon={prefix ?? <SvgLocation color="#595959" />}
						onClear={() => onSelect(null)}
						onDropdownVisibleChange={(o) => o && setOpen(o)}
						dropdownStyle={{ padding: "4px 0" }}
						data-testid={dataTestId}
						{...props}
					/>
				</div>
				{!!error && (
					<RATypography variant="body3" color="#BF1A2F" top={4}>
						{error}
					</RATypography>
				)}
			</div>
		);
	},
);

const StyledSelect = styled(Select)`
	width: 100%;
	font-family: DMSans-Regular;
	font-size: 14px;
	color: #65686f;
	&.ant-select .ant-select-selector {
		height: 52px !important;
		padding: 0 20px 0 44px !important;
		border-radius: 12px;
	}
	&.ant-select .ant-select-selection-item {
		display: flex;
		align-items: center;
		padding-inline-end: 0;
		color: #595959;
	}
	.ant-select-selection-search,
	.ant-select-selection-placeholder {
		display: flex;
		align-items: center;
		inset-inline-start: 44px !important;
		inset-inline-end: 40px !important;
	}
	.ant-select-arrow {
		inset-inline-start: 16px;
	}
	.ant-select-clear {
		inset-inline-end: 16px;
	}
`;

const DropdownRender = styled.div`
	.ant-select-item {
		border-radius: 0 !important;
		padding: 0 !important;
	}
	.ant-select-item-option-active {
		font-weight: 400 !important;
	}
`;

const LoadingElement = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1;
	background-color: #ffffff;
	border: 1px solid #e9e8e8;
	border-radius: 12px;
`;

const CollapseLabel = styled(Collapse)`
	.ant-collapse-header {
		padding: 12px 20px !important;
		&:hover {
			background-color: rgba(34, 34, 34, 0.04);
		}
	}
	.ant-collapse-expand-icon {
		padding-inline-start: 0 !important;
	}
	.ant-collapse-content-box {
		padding: 0 !important;
		border-top: 1px solid #e9e8e8;
	}
	.ant-collapse-header-text {
		max-width: calc(100% - 24px);
	}
`;

const CollapseArrow = styled.div`
	width: 22px;
	height: 22px;
	background-color: rgba(34, 34, 34, 0.08);
	border-radius: 22px;
	justify-content: center;
`;

const NearAirportWrapper = styled.div`
	padding: 12px 20px;
	&:hover {
		background-color: rgba(34, 34, 34, 0.04);
	}
`;
